/* eslint-disable new-cap */
/* eslint-disable no-inline-comments */
/* eslint-disable line-comment-position */
/* eslint-disable max-len */
/* eslint-disable max-statements */
import { BlueprintInstanceType } from '../shared.enums';
import { Breakpoints } from '../breakpoints/breakpoints.class';
import { IslandType } from '../public.enums';
import { PanelConnectionType, PanelType } from './enums';
import { panelData } from './panel.data';
import { PanelItemGroups } from '../panel-item-groups/panel-item-groups.class';
import { Publish } from '../../decorators';
import { Axis, degrees2radians, Vertex3 } from '../../spatial-classes';
import {
	distributeApplianceGroupsHorizontallyCfn,
	getHorizontalIntersectionAreasCfn,
	getSidePointsCfn,
	GetSidePointsParamsCfn,
	getVerticalIntersectionAreasCfn,
	invertConnectionTypeCfn,
	isBackPanelCfn,
	isFrontPanelCfn,
	isLeftPanelCfn,
	isRightPanelCfn,
	newIslandCfn,
	getPanelOverlapCfn,
	getUsablePanelWidthCfn,
	getOccupiedWidthCfn,
	getAvailableWidthCfn,
	getMinimumPanelWidthCfn,
	getPanelLayoutInputCfn,
	placeGroupAtCfn,
	swapApplianceGroupPositionsCfn,
	fineTuneGroupPositionCfn,
	resetFineTuneCfn,
	FineTuneGap,
	PanelLayoutInput
} from './functions';
import { GetIntersectionAreasOptions, PanelConnectionsData, PanelCorners, PanelDataIn, PanelDataOut } from './types';
import { addUtilityPanelCutouts, getPanelCenter, getPanelCornerPoints, GetPanelCornerPointsOptions } from './helpers';
import { IntersectionAreas, ReferenceMap } from '../shared.types';
import { KitchenDesignBase, KitchenDesignOptions } from '../kitchen-design-base';

import type { Size } from '../../types';

export class Panel extends KitchenDesignBase<PanelDataIn> {
	readonly connections: PanelConnectionsData;
	readonly panelItemGroups: PanelItemGroups;
	readonly breakpoints: Breakpoints;
	readonly type: PanelType;

	#size: Size;
	#countertopOverhang: number;
	#isUtilityPanel: boolean;

	public label: string;

	constructor (options: KitchenDesignOptions<PanelDataIn> = {}) {
		super(BlueprintInstanceType.Panel, options);

		const { dataIn = null } = options;
		const data = panelData(dataIn);

		// initialize private properties
		this._id = data.id;
		this.type = data.type;
		this.label = data.label;
		this.#countertopOverhang = data.countertopOverhang;
		this.#isUtilityPanel = data.isUtilityPanel ?? false;

		// initialize metadata - use size from dataIn if provided, otherwise default to zeros
		this.#size = data.size ?? { width: 0, height: 0, depth: 0 };

		// initialize readonly properties
		this.connections = data.connections;
		this.panelItemGroups = new PanelItemGroups({ dataIn: data.panelItemGroups, parent: this });
		this.breakpoints = new Breakpoints({ dataIn: data.breakpoints, parent: this });

		// Only mark as initialized if this is the actual Panel class (not a subclass like Backsplash)
		// Subclasses are responsible for calling setInitialized() after their own initialization
		if (this.constructor === Panel) {
			this.setInitialized();
		}
	}

	/**
	 * panel center represents the position relative to the parent island
	 * @returns Vertex3 representing the center position relative to the parent island
	 */
	get center (): Vertex3 {
		// const cp = this.context.cache.match<Vertex3>(
		// 	[this.id, 'getPanelCenterCfn'],
		// 	() => [
		// 		'getPanelCenterCfn',
		// 		getPanelCenterCfn(this, { island: this.refMap.island, nearestSixteenth: true }, new Set())
		// 	]
		// );

		const cp = getPanelCenter(this, { nearestSixteenth: true }, new Set());

		if (this.refMap.island) {
			cp.z += this.refMap.island.zCenterOffset;
		}

		// console.log('Panel.center', this.label, cp.center.serialize());
		return cp;
	}

	/**
	 * panel origin represents the world space position of the panel
	 * origin = parentIsland.origin + this.center
	 * @returns Vertex3 representing the world space position
	 */
	get origin (): Vertex3 {
		const origin = new Vertex3();
		if (this.refMap.island) {
			origin.moveTo(this.refMap.island.origin);
			origin.add(this.center);
		}
		return origin;
	}

	/**
	 * Set the countertop overhang for the panel
	 * @param overhang number
	 * @emits panel_overhang.updated
	 */
	@Publish('overhang', 'updated')
	set countertopOverhang (overhang: number) {
		if (Number.isNaN(overhang)) {
			this.context.logger.warn(`unable to set countertop overhang for panel, value provided (${overhang}) is not a number.`);
		} else {
			this.#countertopOverhang = overhang;
		}
	}
	get countertopOverhang (): number {
		return this.#countertopOverhang;
	}

	/**
	 * Set whether this panel is a utility panel
	 * @param value boolean
	 * @emits panel_utility.updated
	 */
	@Publish('panel_utility', 'updated')
	set isUtilityPanel (value: boolean) {
		this.#isUtilityPanel = value;
	}

	get isUtilityPanel (): boolean {
		if (!this.#isUtilityPanel) return false;
		if (this.isFrontPanel) return false;

		const hasShowLabelItems = this.panelItemGroups.children.some((group) => group.items.children.some((item) => item.item.showLabel || item.item.category?.isUtilityCutout));

		if (!hasShowLabelItems) {
			return false;
		}

		return true;
	}

	// calculate these:
	get hasPassthrough (): boolean {
		return this.panelItemGroups.children.some((g) => g.hasPassthrough);
	}
	get hasVents (): boolean {
		return this.panelItemGroups.children.some((g) => g.hasVents);
	}
	get isLeftPanel (): boolean {
		return isLeftPanelCfn(this, this.refMap.panels?.frontPanelId ?? null);
	}
	get isRightPanel (): boolean {
		return isRightPanelCfn(this, this.refMap.panels?.frontPanelId ?? null);
	}
	get isSidePanel (): boolean {
		return this.isLeftPanel || this.isRightPanel;
	}
	get isBackPanel (): boolean {
		return isBackPanelCfn(this, this.refMap.panels?.frontPanelId ?? null);
	}
	get isFrontPanel (): boolean {
		return isFrontPanelCfn(this, this.refMap.panels?.frontPanelId ?? null);
	}

	get hasSourceConnection (): boolean {
		if (!this.refMap.island) return false;
		const connections = this.refMap.island.connections.getConnectionsBySourcePanelId(this.id);
		return connections.length > 0;
	}
	get hasTargetConnection (): boolean {
		if (!this.refMap.island) return false;
		const connections = this.refMap.island.connections.findConnectionsByTargetIslandId(this.id);
		return connections !== null;
	}

	/**
	 * Horizontal space accounting. There was no accessor for any of this, so
	 * every caller that needed "how much room is left?" open-coded its own sum
	 * over `maxOutsideWidth` and applied its own spacing constant — the add
	 * gate, the resize floor, clean-kitchen's auto-size target and the
	 * design-context the AI reads all disagreed, and only the distributor
	 * accounted for connected-island overlap at all.
	 */
	get overlapWidth (): number {
		return getPanelOverlapCfn(this as Panel).total;
	}
	/** Panel width minus what connected islands consume at either end. */
	get usableWidth (): number {
		return getUsablePanelWidthCfn(this as Panel);
	}
	/** Width taken by this panel's appliance groups (outside dimensions). */
	get occupiedWidth (): number {
		return getOccupiedWidthCfn(this as Panel);
	}
	/** Widest new appliance group this panel can still accept. */
	get availableWidth (): number {
		return getAvailableWidthCfn(this as Panel);
	}
	/** Narrowest this panel can be and still hold what is on it. */
	get minimumWidth (): number {
		return getMinimumPanelWidthCfn(this as Panel);
	}

	get thickness (): number {
		return this.#size.depth;
	}

	/**
	 * panel axis represents the rotation/orientation of the panel
	 * @returns Axis representing the panel's rotation
	 */
	get axis (): Axis {
		const angle = this.getOriginAngle();
		return new Axis({ dataIn: { x: 0, y: angle, z: 0 }});
	}

	/**
	 * Set the size of the panel, this is typically only set during island creation
	 * @param size Size
	 * @emits panel_size.updated
	 */
	@Publish('panel_size', 'updated')
	set size (size: Size) {
		this.#size = size;
	}
	get size (): Size {
		return this.#size;
	}

	// eslint-disable-next-line max-params
	public connectLeftSideToPanel (targetPanel: Panel, connectionType: PanelConnectionType, angle: number, connectionTargetType: PanelConnectionType | null = null): void {
		// console.log('Panel.connectLeftSideToPanel()', targetPanel, connectionType, angle);
		this.connections.left = {
			targetPanelId: targetPanel.id,
			type: connectionType,
			angle
		};

		if (targetPanel.connections.right?.targetPanelId !== this.id) {
			const targetType = connectionTargetType ? connectionTargetType : invertConnectionTypeCfn(connectionType);
			targetPanel.connectRightSideToPanel(this, targetType, -angle);
		}
	}

	public connectRightSideToPanel (targetPanel: Panel, connectionType: PanelConnectionType, angle: number): void {
		// console.log('Panel.connectLeftSideToPanel()', targetPanel, connectionType, angle);

		this.connections.right = {
			targetPanelId: targetPanel.id,
			type: connectionType,
			angle
		};

		if (targetPanel.connections.left?.targetPanelId !== this.id) {
			targetPanel.connectLeftSideToPanel(this, invertConnectionTypeCfn(connectionType), -angle);
		}
	}
	public getHorizontalIntersectionAreas (options: GetIntersectionAreasOptions): IntersectionAreas {
		return getHorizontalIntersectionAreasCfn(this as Panel, options);
	}
	public getVerticalIntersectionAreas (options: GetIntersectionAreasOptions): IntersectionAreas {
		return getVerticalIntersectionAreasCfn(this as Panel, options);
	}

	/**
	 * Locates the panel connected to the left of this panel
	 * @returns Panel
	 */
	public getLeftPanel (): Panel | null {
		return this.refMap.panels && this.connections.left ? this.refMap.panels.findById(this.connections.left.targetPanelId) : null;
	}

	/**
	 * Locates the panel connected to the right of this panel
	 * @returns Panel
	 */
	public getRightPanel (): Panel | null {
		return this.refMap.panels && this.connections.right ? this.refMap.panels.findById(this.connections.right.targetPanelId) : null;
	}

	/**
	 * gets all panels off the left and right panels connections
	 * @returns Array of Panel
	 */
	public getBackPanels (): Array<Panel> {
		return this.refMap.panels
			? (this.refMap.panels.children.filter((p) => p.id !== this.id && p.id !== this.getLeftPanel()?.id && p.id !== this.getRightPanel()?.id) as Array<Panel>)
			: [];
	}

	/**
	 * evenly distribute the appliance groups horizontally
	 */
	@Publish('panel', 'updated')
	public distributeApplianceGroupsHorizontally (): void {
		distributeApplianceGroupsHorizontallyCfn(this as Panel);
	}

	/**
	 * The horizontal planner's view of this panel (widths, locks, overlap). Feed it
	 * to `planGroupLayoutCfn` to ask where the columns WOULD sit without moving them.
	 */
	get layoutInput (): PanelLayoutInput {
		return getPanelLayoutInputCfn(this as Panel);
	}

	/**
	 * Drops one appliance column at an absolute x and re-spaces the panel; the
	 * ORDER of x values decides the slot, the planner decides the exact position.
	 * @emits panel.updated
	 */
	@Publish('panel', 'updated')
	public placeGroupAt (groupId: string, x: number): void {
		placeGroupAtCfn(this as Panel, { groupId, x });
	}

	/**
	 * Positional swap: two columns on this panel trade slots. Both stay.
	 * @emits panel.updated
	 */
	@Publish('panel', 'updated')
	public swapApplianceGroupPositions (groupIdA: string, groupIdB: string): void {
		swapApplianceGroupPositionsCfn(this as Panel, { groupIdA, groupIdB });
	}

	/**
	 * Puts one column exactly at x between the same two neighbours and marks it
	 * fine-tuned, so the distributor keeps it there. No re-spacing. Returns the
	 * room it had (its neighbours and the clamped range).
	 * @emits panel.updated
	 */
	@Publish('panel', 'updated')
	public fineTuneGroupPosition (groupId: string, x: number): FineTuneGap {
		return fineTuneGroupPositionCfn(this as Panel, { groupId, x });
	}

	/** Forget every hand-placed position on this panel; the next distribute re-spaces the run. */
	public resetFineTune (): void {
		resetFineTuneCfn(this as Panel);
	}

	/**
	 * Adds utility panel cutouts to this panel based on its type (back panel or side panel).
	 * @emits panel.updated
	 */
	@Publish('panel', 'updated')
	public addUtilityCutouts (): void {
		addUtilityPanelCutouts(this as Panel);
	}

	/**
	 * Treat the front panel as the origin plane, what is the angular (degrees) relationship from this panel
	 * @returns Angle in degrees
	 */
	public getOriginAngle (inRadians = false, visitedPanels: Set<string> = new Set()): number {
		if (!this.refMap.panels) return 0;
		if (this.isFrontPanel) return 0;
		if (!this.connections.left) return 0;

		// Prevent infinite recursion by tracking visited panels
		if (visitedPanels.has(this.id)) {
			this.context.logger.warn(`Circular connection detected in panel ${this.id}, returning 0 for getOriginAngle`);
			return 0;
		}

		const localFn = () => {
			const leftPanel = this.refMap?.panels?.findById(this.connections?.left?.targetPanelId ?? null);
			if (!leftPanel) return 0;

			// Add current panel to visited set and pass it to the recursive call
			visitedPanels.add(this.id);
			const angle = (this.connections?.left?.angle ?? 0) + (leftPanel?.getOriginAngle(false, visitedPanels) ?? 0);
			return inRadians ? degrees2radians(angle) : angle;
		};
		return localFn();
		// return this.context.cache.match<number>([this.id, 'getOriginAngle'], () => ['getOriginAnglee', localFn()]);
	}

	/**
	 * Method to calculate the panels corner points and center point relative to the island's center point
	 * @param inside If true, the calculation will use the inside face of the panel.
	 * @param options Options for calculating the corner points
	 * @param visitedPanels Set of panel IDs to prevent infinite recursion
	 * @returns Panel's corners and center location
	 */
	public getCornerPoints (inside = null, options: GetPanelCornerPointsOptions = {}, visitedPanels: Set<string> = new Set()): PanelCorners | null {
		// return measurePerformance('Panel.getCornerPoints', () => getCornerPoints(this, { island: this.refMap.island, inside, ...options }));
		// return this.context.cache.match<PanelCorners>([this.id, `getCornerPoints:${inside}`], () => [`getCornerPoints:${inside}`, getCornerPoints(this, { island: this.refMap.island, inside, ...options }, visitedPanels)])
		// Return null if no island parent
		if (!this.refMap.island) {
			return null;
		}

		// const islandCenter = this.refMap.island?.center ?? new Vertex3();
		const cpLocal = getPanelCornerPoints(this, { inside, ...options, lock: false }, visitedPanels);

		// getPanelCornerPoints are calculated using the front panel as local origin,
		// translate the points to the island's center using the uniform z-center offset
		if (this.refMap.island) {
			const zOffset = this.refMap.island.zCenterOffset;
			cpLocal.corners.forEach((corner) => { corner.z += zOffset; });
			cpLocal.center.z += zOffset;
		}

		// console.log('getCornerPoints', this.label, islandCenter.serialize(), cpLocal.corners.map((corner) => corner.serialize()), cpIsland.corners.map((corner) => corner.serialize()));
		// console.log('panel.getCornerPoints', this.label, inside, { corners: cpLocal.corners.map((corner) => corner.serialize()), center: cpLocal.center.serialize(), axis: cpLocal.axis.serialize() });
		return cpLocal;
	}

	/**
	 * @param id id to find child reference for
	 * @returns ReferenceMap
	 */
	public getReferenceMap (id: string): ReferenceMap {
		return super.getReferenceMap(id, ['breakpoints', 'panelItemGroups']);
	}

	/**
	 * Method to calculate the panel side corner points and center point based on the island's center point
	 * @returns
	 *  Panel's side corner points and center location
	 */
	public getSidePoints (options: GetSidePointsParamsCfn = {}): PanelCorners | null {
		// return measurePerformance('Panel.getSidePoints', () => getSidePointsCfn(this, options));
		const island = this.refMap.island ?? null;
		const obstruction = this.refMap.obstruction ?? null;

		// Return null if no island parent
		if (!island && !obstruction) {
			return null;
		}

		const spLocal = getSidePointsCfn(this, options);

		if (island && spLocal && options.translate) {
			const zOffset = island.zCenterOffset;
			spLocal.corners.forEach((corner) => { corner.z += zOffset; });
			spLocal.center.z += zOffset;
		} else if (obstruction && spLocal && options.translate) {
			// islandSize is the calculated size of the island's outside dimensions
			const { obstructionSize } = obstruction;

			// add the island size to the corner points and center point, making the corner points relative to the island's center
			spLocal.corners.forEach((corner) => { corner.z += (obstructionSize.depth / 2) - (this.thickness / 2); });
			spLocal.center.z += (obstructionSize.depth / 2) - (this.thickness / 2);
		}

		return spLocal;
	}

	/**
	 * Created a new island and connects it to this panel
	 * @param islandType IslandType for new island
	 * @param size Size | Cornersize for island to be created
	 */
	public newIsland (islandType: IslandType, size: Size): void {
		newIslandCfn(this, { islandType, size });
	}

	/**
	 * Serialize the panel data
	 * @returns PanelData
	 */
	public serialize (): PanelDataOut {
		return {
			id: this._id,
			type: this.type,
			label: this.label,
			countertopOverhang: this.#countertopOverhang,
			isUtilityPanel: this.#isUtilityPanel,
			panelItemGroups: this.panelItemGroups.serialize(),
			breakpoints: this.breakpoints.serialize(),
			connections: this.connections,
			metadata: {
				size: this.#size
			}
		};
	}
}
